/**
 * @fileoverview FUNCTIONAL VIEW COMPONENT FOR COMPANIES DATA DISPLAY
 * @active Used in current Next.js App Router routes
 *
 * This component is responsible for the actual implementation of the companies data display.
 * It is the CURRENT ACTIVE IMPLEMENTATION used in the App Router routes.
 *
 * Key responsibilities:
 * 1. View switching logic between TABLE VIEW and CARD VIEW
 * 2. Data fetching and Redux store management for companies data
 * 3. Conditional rendering of the appropriate view component based on user selection
 *
 * CURRENT APP ROUTER COMPONENT HIERARCHY:
 * - page.tsx: Server component that fetches data
 *   └─ CompaniesWrapper.client.tsx: Client-side wrapper with "use client" directive
 *      └─ NewCompanies.tsx (THIS FILE): Implements the view switching and data display
 *         ├─ NewCompaniesList: TABLE VIEW implementation (when isCards=false)
 *         └─ NewCompaniesCards: CARD VIEW implementation (when isCards=true)
 *
 * The view switching is controlled by the `isCards` state from Redux, which is
 * toggled by the SwitchViewBtns component:
 * - When isCards=false: Renders the TABLE VIEW (NewCompaniesList)
 * - When isCards=true: Renders the CARD VIEW (NewCompaniesCards)
 *
 * This component replaced the legacy CompaniesList.tsx implementation which is no longer used.
 *
 * @see {@link src/app/(main)/(home)/(research)/companies/page.tsx} - Current App Router page component
 * @see {@link src/components/companies/CompaniesWrapper.client.tsx} - Client wrapper used in App Router
 * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - TABLE VIEW implementation
 * @see {@link src/components/companies/newCompaniesCards/index.tsx} - CARD VIEW implementation
 * @see {@link src/components/companies/newCompaniesCards/SwitchViewBtns.tsx} - Component that toggles between views
 * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Redux slice for companies data
 *
 * @note This component replaces the deprecated implementation in src/components/companies/CompaniesList.tsx
 * @see {@link src/components/companies/CompaniesList.tsx} - DEPRECATED implementation
 * @see {@link src/components/ui/cardCompanies/CardCompanies.jsx} - DEPRECATED card view implementation
 * @see {@link src/components/companies/companiesCard/CompaniesCard.jsx} - DEPRECATED card container
 * @see {@link src/components/companies/Companies.tsx} - DEPRECATED page component
 */

"use client";

import { memo, useEffect, useMemo } from "react";

import { Flex } from "@chakra-ui/react";
import { useParams } from "next/navigation";
import { useSelector } from "react-redux";

import { useActions } from "@/hooks/useActions";
import useGetCurrentView from "@/hooks/useGetCurrentView";
import useInitializeDataToStore from "@/hooks/useInitializeDataToStore";
import newCompaniesListSelectors from "@/store/newCompaniesList/newCompaniesList.selectors";
import type { Company } from "@/types/company";

import { companyFieldMatching } from "./companiesList/dataStore/correspondingFieldToHeader.jsx";
import NewCompaniesList from "./companiesList/newCompaniesList/index";
import NewCompaniesCards from "./newCompaniesCards";
import SwitchViewBtns from "./newCompaniesCards/SwitchViewBtns";

/**
 * Type definitions for component props and state
 */
interface NewCompaniesProps {
  newCompanies: {
    data: Company[];
    page: number;
    pages: number;
    total: number;
  };
  filters: Array<{
    key: string;
    value: string | number | boolean;
    operator?: string;
  }>;
}

/**
 * Active implementation for companies data display with view switching
 *
 * This component is the current active implementation for displaying companies data.
 * It handles view switching between TABLE VIEW and CARD VIEW based on the isCards state.
 *
 * @param {NewCompaniesProps} props - Component props
 * @param {Object} props.newCompanies - Companies data with pagination info
 * @param {Array} props.filters - Filters for the companies list
 * @returns {JSX.Element} Rendered component
 *
 * @see {@link src/components/companies/CompaniesList.tsx} - DEPRECATED implementation this replaces
 */
const NewCompanies = ({ newCompanies, filters }: NewCompaniesProps) => {
  const {
    setNewCompanies,
    setListParams,
    setCLFiltersList,
    setArrayFilters,
    setViewParams,
    clearFilterArr,
    setIsLoadingCompanies,
  } = useActions();
  const { currentView } = useGetCurrentView();

  const params = useParams() as { tag?: string };
  const isCards = useSelector(newCompaniesListSelectors.getIsCompaniesCards());

  /**
   * Initialize loading state on component mount and reset when data is available
   *
   * This effect sets the loading state to true when the component mounts,
   * and resets it to false once the companies data is available.
   * Setting the loading state at the top level component ensures the loading UI is shown
   * as soon as possible in the component lifecycle.
   *
   * @see {@link src/components/ui/WaveSpinner.tsx} - Loading spinner component
   * @see {@link src/store/newCompaniesList/newCompaniesList.slice.ts} - Loading state management
   * @see {@link src/components/companies/companiesList/newCompaniesList/index.tsx} - Where the loading UI is rendered
   */
  useEffect(() => {
    // Only show loading state if companies data is not yet available
    if (!newCompanies || newCompanies.data.length === 0) {
      setIsLoadingCompanies(true);
    } else {
      // Reset loading state when companies data is available
      setIsLoadingCompanies(false);
    }

    return () => {
      setIsLoadingCompanies(false);
    };
  }, [newCompanies, setIsLoadingCompanies]);

  const companies = useSelector(
    newCompaniesListSelectors.getNewCompaniesList()
  );
  const normalizeData = useMemo((): Company[] => {
    return (
      companies?.map(
        (company: Company) =>
          companyFieldMatching(company) as unknown as Company
      ) || []
    );
  }, [companies]);

  useInitializeDataToStore({
    action: setNewCompanies,
    data: { data: newCompanies?.data },
  });
  useInitializeDataToStore({
    action: setCLFiltersList,
    data: filters,
  });
  useInitializeDataToStore({
    action: setListParams,
    data: {
      page: newCompanies?.page,
      pages: newCompanies?.pages,
      total: newCompanies?.total,
    },
  });

  useEffect(() => {
    clearFilterArr({ key: currentView?.id });
    setViewParams({
      key: currentView?.id,
      params: {
        arrayFilters: undefined,
      },
    });
    // Using global setTimeout with proper typing
    const timer = window.setTimeout(() => {
      if (params?.tag) {
        const filters = {
          ["Sector / Industry Tags"]: [params?.tag],
        };

        setArrayFilters({
          key: currentView?.id,
          filters,
        });
        setViewParams({
          key: currentView?.id,
          params: {
            arrayFilters: filters,
          },
        });
      }
    }, 10);

    // Using global clearTimeout with proper typing
    return () => window.clearTimeout(timer);
  }, [
    clearFilterArr,
    currentView?.id,
    params?.tag,
    setArrayFilters,
    setViewParams,
  ]);

  return (
    <>
      {!isCards ? (
        <Flex align={"center"} justify={"end"} px={"20px"} py={"8px"}>
          <SwitchViewBtns />
        </Flex>
      ) : null}

      {!isCards ? (
        <NewCompaniesList
          companies={companies as unknown as Company[]}
          normalizeData={normalizeData}
        />
      ) : (
        <NewCompaniesCards
          filters={filters}
          companies={normalizeData as unknown as Company[]}
        />
      )}
    </>
  );
};

export default memo(NewCompanies);
